import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useClients } from '../../hooks/useClients';
import LoadingSpinner from '../shared/LoadingSpinner';
import { useUser } from '../../contexts/UserContext';
import adminApi from '../../services/adminApi';
import ResetPassword from './ResetPassword';
import MigrationLogs from './MigrationLogs';

function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('clients');
  const [showInactive, setShowInactive] = useState(false);
  const [showInactiveConsultants, setShowInactiveConsultants] = useState(false);
  const [consultants, setConsultants] = useState([]);
  const [consultantLoading, setConsultantLoading] = useState(false);
  const [consultantError, setConsultantError] = useState(null);
  const [shouldShowConsultants, setShouldShowConsultants] = useState(false);
  const [selectedConsultant, setSelectedConsultant] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [dialogType, setDialogType] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { clients, loading, error, deleteClient, activateClient } = useClients(true);
  const { user } = useUser();

  useEffect(() => {
    if (location.state?.showConsultants) {
      setShouldShowConsultants(true);
      // Ryd location state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const userPermissions = user ? {
    isAdmin: user.role === 'admin',
    canManageClients: user.role === 'admin',
    canCreateClients: user.role === 'admin',
    canEditClients: user.role === 'admin',
    canActivateClients: user.role === 'admin',
    canDeactivateClients: user.role === 'admin',
    canManageConsultants: user.role === 'admin',
    canCreateConsultants: user.role === 'admin',
    canDeactivateConsultants: user.role === 'admin',
    canActivateConsultants: user.role === 'admin'
  } : {
    isAdmin: false,
    canManageClients: false,
    canCreateClients: false,
    canEditClients: false,
    canActivateClients: false,
    canDeactivateClients: false,
    canManageConsultants: false,
    canCreateConsultants: false,
    canDeactivateConsultants: false,
    canActivateConsultants: false
  };

  useEffect(() => {
    if (shouldShowConsultants) {
      setActiveTab('consultants');
      setShouldShowConsultants(false);
    }
  }, [shouldShowConsultants]);

  useEffect(() => {
    const fetchConsultants = async () => {
      if (userPermissions.canManageConsultants) {
        setConsultantLoading(true);
        try {
          const data = await adminApi.getConsultants();
          setConsultants(data);
        } catch (err) {
          setConsultantError(err.message);
        } finally {
          setConsultantLoading(false);
        }
      }
    };
    fetchConsultants();
  }, [userPermissions.canManageConsultants, activeTab]);

  const handleDeleteConsultant = async (consultantId) => {
    if (!userPermissions.canDeactivateConsultants) {
      return;
    }

    const consultant = consultants.find(c => c.id === consultantId);
    setSelectedConsultant(consultant);
    setDialogType('deleteConsultant');
    setIsOpen(true);
  };

  const confirmDeleteConsultant = async () => {
    try {
      await adminApi.deleteConsultant(selectedConsultant.id);
      setConsultants(consultants.map(c => 
        c.id === selectedConsultant.id ? { ...c, active: false } : c
      ));
    } catch (error) {
      console.error('Fejl ved deaktivering:', error);
    } finally {
      setIsOpen(false);
      setSelectedConsultant(null);
      setDialogType(null);
    }
  };

  const handleActivateConsultant = async (consultantId) => {
    if (!userPermissions.canActivateConsultants) {
      return;
    }

    const consultant = consultants.find(c => c.id === consultantId);
    setSelectedConsultant(consultant);
    setDialogType('activateConsultant');
    setIsOpen(true);
  };

  const confirmActivateConsultant = async () => {
    try {
      await adminApi.activateConsultant(selectedConsultant.id);
      setConsultants(consultants.map(c => 
        c.id === selectedConsultant.id ? { ...c, active: true } : c
      ));
    } catch (error) {
      console.error('Fejl ved aktivering:', error);
    } finally {
      setIsOpen(false);
      setSelectedConsultant(null);
      setDialogType(null);
    }
  };

  const handleDelete = async (clientId) => {
    if (!userPermissions.canDeactivateClients) {
      return;
    }

    const client = clients.find(c => c.id === clientId);
    setSelectedClient(client);
    setDialogType('deleteClient');
    setIsOpen(true);
  };

  const confirmDeleteClient = async () => {
    const success = await deleteClient(selectedClient.id);
    if (success) {
      console.log('Kunde deaktiveret');
    } else {
      console.error('Fejl ved deaktivering:', error);
    }
    setIsOpen(false);
    setSelectedClient(null);
    setDialogType(null);
  };

  const handleActivate = async (clientId) => {
    if (!userPermissions.canActivateClients) {
      return;
    }

    const client = clients.find(c => c.id === clientId);
    setSelectedClient(client);
    setDialogType('activateClient');
    setIsOpen(true);
  };

  const confirmActivateClient = async () => {
    const success = await activateClient(selectedClient.id);
    if (success) {
      console.log('Kunde aktiveret');
    } else {
      console.error('Fejl ved aktivering:', error);
    }
    setIsOpen(false);
    setSelectedClient(null);
    setDialogType(null);
  };

  const filteredConsultants = consultants.filter(consultant => consultant.active === !showInactiveConsultants);

  const filteredClients = clients.filter(client => client.active === !showInactive);

  if (loading) return <LoadingSpinner />;
  if (error) return <div>Fejl: {error}</div>;

  return (
    <div className="settings">
      <h2>Administrator Dashboard</h2>
      <div className="tabs">
        <button className={activeTab === 'clients' ? 'active' : ''} onClick={() => setActiveTab('clients')}>
          Kunder
        </button>
        <button className={activeTab === 'consultants' ? 'active' : ''} onClick={() => setActiveTab('consultants')}>
          Konsulenter
        </button>
        <button className={activeTab === 'migration-logs' ? 'active' : ''} onClick={() => setActiveTab('migration-logs')}>
          Migration
        </button>
        <button className={activeTab === 'reset-password' ? 'active' : ''} onClick={() => setActiveTab('reset-password')}>
          Nulstil adgangskode
        </button>
      </div>
      <div className="tab-content">
        {activeTab === 'clients' ? (
          <>
            {userPermissions.canManageClients && (
              <button
                className="button button-secondary"
                onClick={() => setShowInactive(!showInactive)}
              >
                {showInactive ? 'Vis aktive kunder' : 'Vis deaktiverede kunder'}
              </button>
            )}
            {userPermissions.canCreateClients && (
              <button
                className="button button-primary"
                onClick={() => navigate('/admin/create-client')}
                style={{ marginLeft: '10px' }}
              >
                Opret ny kunde
              </button>
            )}
            <div>
              <table>
                <thead>
                  <tr>
                    <th>Navn</th>
                    <th>Database</th>
                    <th>Blob Storage</th>
                    <th>Antal brugere</th>
                    {userPermissions.canManageClients && <th>Handlinger</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map((client) => (
                    <tr key={client.id}>
                      <td>{client.name}</td>
                      <td>{client.databaseName}</td>
                      <td>{client.blobContainer}</td>
                      <td>{client.userCount}</td>
                      {userPermissions.canManageClients && (
                        <td>
                          <div className="button-group">
                            {userPermissions.canEditClients && (
                              <button 
                                className="button button-secondary" 
                                onClick={() => navigate(`/admin/edit-client/${client.id}`)}
                              >
                                Rediger
                              </button>
                            )}
                            {showInactive ? (
                              userPermissions.canActivateClients && (
                                <button 
                                  className="button button-primary" 
                                  onClick={() => handleActivate(client.id)}
                                >
                                  Aktiver
                                </button>
                              )
                            ) : (
                              userPermissions.canDeactivateClients && (
                                <button 
                                  className="button button-warning" 
                                  onClick={() => handleDelete(client.id)}
                                >
                                  Deaktiver
                                </button>
                              )
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : activeTab === 'consultants' ? (
          <>
            {userPermissions.canManageConsultants && (
              <button
                className="button button-secondary"
                onClick={() => setShowInactiveConsultants(!showInactiveConsultants)}
              >
                {showInactiveConsultants ? 'Vis aktive konsulenter' : 'Vis deaktiverede konsulenter'}
              </button>
            )}
            {consultantLoading ? (
              <LoadingSpinner />
            ) : consultantError ? (
              <div>Fejl: {consultantError}</div>
            ) : (
              <div>
                <table>
                  <thead>
                    <tr>
                      <th>Navn</th>
                      <th>Email</th>
                      <th>Oprettet</th>
                      <th>Opdateret</th>
                      <th>Handlinger</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredConsultants.map((consultant) => (
                      <tr key={consultant.id}>
                        <td>{consultant.name}</td>
                        <td>{consultant.email}</td>
                        <td>{new Date(consultant.createdAt).toLocaleDateString('da-DK')}</td>
                        <td>{new Date(consultant.updatedAt).toLocaleDateString('da-DK')}</td>
                        <td>
                          <div className="button-group">
                            {showInactiveConsultants ? (
                              userPermissions.canActivateConsultants && (
                                <button 
                                  className="button button-primary" 
                                  onClick={() => handleActivateConsultant(consultant.id)}
                                >
                                  Aktiver
                                </button>
                              )
                            ) : (
                              userPermissions.canDeactivateConsultants && (
                                <button 
                                  className="button button-warning" 
                                  onClick={() => handleDeleteConsultant(consultant.id)}
                                >
                                  Deaktiver
                                </button>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : activeTab === 'migration-logs' ? (
          <MigrationLogs />
        ) : activeTab === 'reset-password' ? (
          <ResetPassword />
        ) : null}
      </div>
      <div>
        {isOpen && (
          <div className="modal">
            <div className="modal-content">
              <h4>{dialogType === 'deleteConsultant' ? 'Deaktiver konsulent' : dialogType === 'activateConsultant' ? 'Aktiver konsulent' : dialogType === 'deleteClient' ? 'Deaktiver kunde' : 'Aktiver kunde'}</h4>
              <p style={{ marginBottom: '1.5rem', color: '#333' }}>
                {dialogType === 'deleteConsultant' ? `Er du sikker på at du vil deaktivere konsulenten ${selectedConsultant?.name}?` : dialogType === 'activateConsultant' ? `Er du sikker på at du vil aktivere konsulenten ${selectedConsultant?.name}?` : dialogType === 'deleteClient' ? `Er du sikker på at du vil deaktivere kunden ${selectedClient?.name}?` : `Er du sikker på at du vil aktivere kunden ${selectedClient?.name}?`}
              </p>
              <div className="button-group">
                <button onClick={() => setIsOpen(false)} className="button button-secondary">
                  Annuller
                </button>
                <button 
                  className={`button ${dialogType?.includes('delete') ? 'button-warning' : 'button-primary'}`} 
                  onClick={() => {
                    if (dialogType === 'deleteConsultant') confirmDeleteConsultant();
                    if (dialogType === 'activateConsultant') confirmActivateConsultant();
                    if (dialogType === 'deleteClient') confirmDeleteClient();
                    if (dialogType === 'activateClient') confirmActivateClient();
                  }} 
                >
                  {dialogType?.includes('delete') ? 'Deaktiver' : 'Aktiver'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard; 